import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Dress } from '../types';
import dressService from '../services/dress';
import dressLikesService from '../services/dressLikes';
import categoryService, { Category } from '../services/category';
import designerService, { Designer } from '../services/designer';
import MultiSelectFilter from '../components/MultiSelectFilter';
import DressCard from '../components/DressCard';
import { FunnelIcon, HeartIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

const Catalog: React.FC = () => {
  const { user } = useAuth();
  const [dresses, setDresses] = useState<Dress[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedDesignerIds, setSelectedDesignerIds] = useState<string[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [designers, setDesigners] = useState<Designer[]>([]);
  const [likedDressIds, setLikedDressIds] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<string>('default');
  const [showTipBanner, setShowTipBanner] = useState(true);
  const loadingRef = useRef(false);
  
  const observer = useRef<IntersectionObserver | null>(null);
  const lastDressElementRef = useCallback((node: HTMLDivElement | null) => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
        setPage(prevPage => prevPage + 1);
      }
    });
    
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore]);


  // Clear state and disconnect observer when user or filters change
  useEffect(() => {
    // Disconnect observer to prevent unwanted page increments
    if (observer.current) {
      observer.current.disconnect();
    }
    
    // Clear any stale data immediately
    setDresses([]);
    setLikedDressIds(new Set());
    setPage(1);
    setHasMore(true);
    loadingRef.current = false;
  }, [user?.id, user?.catalogAccessLevel, selectedCategories, selectedDesignerIds, sortBy]);

  const loadDresses = useCallback(async (pageNum: number, reset: boolean = false) => {
    // Prevent concurrent API calls
    if (loadingRef.current) return;
    loadingRef.current = true;
    
    try {
      if (pageNum === 1) {
        setLoading(true);
        setDresses([]);
      } else {
        setLoadingMore(true);
      }

      const response = await dressService.getDresses({
        page: pageNum,
        limit: 20,
        categories: selectedCategories.length > 0 ? selectedCategories : undefined,
        designerIds: selectedDesignerIds.length > 0 ? selectedDesignerIds : undefined,
        sortBy: sortBy !== 'default' ? sortBy : undefined,
      });

      if (pageNum === 1 || reset) {
        setDresses(response.data);
      } else {
        setDresses(prev => [...prev, ...response.data]);
      }
      
      setHasMore(response.pagination.page < response.pagination.totalPages);
      
      // Check which dresses are liked
      if (user && response.data.length > 0) {
        const dressIds = response.data.map(d => d.id);
        try {
          const likedIds = await dressLikesService.checkLikes(dressIds);
          setLikedDressIds(prev => new Set([...Array.from(prev), ...likedIds]));
        } catch (error: any) {
          console.error('Failed to check likes:', error);
        }
      }
    } catch (error) {
      console.error('Error loading dresses:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [selectedCategories, selectedDesignerIds, sortBy, user]);

  useEffect(() => {
    // Only load if we haven't already started loading
    if (!loadingRef.current) {
      loadDresses(page, page === 1);
    }
  }, [page, loadDresses]);

  // Load categories and designers
  useEffect(() => {
    const loadFiltersData = async () => {
      try {
        const [categoriesData, designersData] = await Promise.all([
          categoryService.getCategories(),
          designerService.getDesigners()
        ]);
        setCategories(categoriesData);
        setDesigners(designersData);
      } catch (error) {
        console.error('Error loading filter data:', error);
      }
    };

    loadFiltersData();
  }, []);

  // Auto-hide tip banner on scroll
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      // Hide banner after user scrolls down more than 50px
      if (window.scrollY > 50 && showTipBanner) {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          setShowTipBanner(false);
        }, 100); // Small delay to avoid hiding on accidental scrolls
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [showTipBanner]);


  const handleLikeChange = (dressId: string, isLiked: boolean) => {
    setLikedDressIds(prev => {
      const newSet = new Set(prev);
      if (isLiked) {
        newSet.add(dressId);
      } else {
        newSet.delete(dressId);
      }
      return newSet;
    });
  };

  const categoryOptions = categories.map(cat => ({
    id: cat.id,
    label: cat.displayName,
    value: cat.name
  }));

  const designerOptions = designers.map(designer => ({
    id: designer.id,
    label: designer.name,
    value: designer.id
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="px-4 py-4">
          <h1 className="text-2xl font-serif text-gray-900 mb-4">Dress Catalog</h1>
          
          {/* Access Level Notice */}
          {user && user.catalogAccessLevel !== 'exclusive' && user.catalogAccessLevel !== 'partner' && (
            <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
              <div className="flex items-start">
                <div>
                  <p className="text-sm text-green-700">
                    You have limited access to our catalog. To view our <strong>Full Catalog</strong>,{' '}
                    <a
                      href="https://www.theproposal.com.sg/contact/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold underline hover:text-green-800"
                    >
                      Contact Us
                    </a>{' '}
                    for an appointment today!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          {user && showTipBanner && (
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 transition-all duration-300">
              <div className="flex items-start">
                <HeartIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm text-blue-700">
                    <span className="font-semibold">Tip:</span> Double tap any dress to like it instantly!
                    You can also click the heart icon.
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    View all your liked dresses in your{' '}
                    <Link to="/home/liked-dresses" className="font-semibold underline hover:text-blue-800">
                      Liked Dresses
                    </Link>{' '}
                    collection.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Filter Button and Sort */}
        <div className="px-4 py-3 border-t flex items-center justify-between">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 text-gray-700"
          >
            <FunnelIcon className="h-5 w-5" />
            <span>Filters</span>
            {(selectedCategories.length > 0 || selectedDesignerIds.length > 0) && (
              <span className="bg-primary-500 text-white text-xs px-2 py-1 rounded-full">
                Active
              </span>
            )}
          </button>
          
          <div className="flex items-center space-x-2">
            <label htmlFor="sort-select" className="text-sm text-gray-700 font-medium">
              Sort:
            </label>
            <select
              id="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="default">Default</option>
              <option value="latest">Latest</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>
        </div>

        {/* Filters Panel - Inside sticky header so it scrolls together */}
        {showFilters && (
          <div className="bg-white border-t px-4 py-4 space-y-4">
            <MultiSelectFilter
              label="Categories"
              options={categoryOptions}
              selectedValues={selectedCategories}
              onChange={setSelectedCategories}
              placeholder="Select categories..."
            />

            <MultiSelectFilter
              label="Designers"
              options={designerOptions}
              selectedValues={selectedDesignerIds}
              onChange={setSelectedDesignerIds}
              placeholder="Select designers..."
            />

            <button
              onClick={() => {
                setSelectedCategories([]);
                setSelectedDesignerIds([]);
                setSortBy('default');
                setShowFilters(false);
              }}
              className="text-primary-600 text-sm font-medium"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </header>

      {/* Dress Grid */}
      <div className="px-4 py-4">
        {loading && page === 1 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
                <div className="aspect-[3/5] bg-gray-200"></div>
                <div className="p-4">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {dresses.map((dress, index) => (
                <div
                  key={dress.id}
                  ref={index === dresses.length - 1 ? lastDressElementRef : null}
                >
                  <DressCard
                    dress={dress}
                    isLiked={likedDressIds.has(dress.id)}
                    onLikeChange={handleLikeChange}
                  />
                </div>
              ))}
            </div>

            {loadingMore && (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              </div>
            )}

            {!hasMore && dresses.length > 0 && (
              <p className="text-center text-gray-500 py-8">
                You've reached the end of the catalog
              </p>
            )}

            {dresses.length === 0 && !loading && (
              <div className="text-center py-12">
                <p className="text-gray-500">No dresses found matching your criteria</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Catalog;